<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePdf } from "#/composables/usePdfMode";
import { useDecorationStore } from "#/store/decoration";
import { useScheduleStore } from "#/store/schedule";
import { useThemeStore } from "#/store/theme";
import NotificationWindow from "#/ui/notificationWindow/NotificationWindow.vue";
import { buildCartProductFromCardState } from "#/utils/buildCartProduct";
import cartAddIcon from "../../../assets/icons/cart_add_icon.svg";
import NumberModifier from "../../../semantics/cardProduct/NumberModifier.vue";
import { useCardStore } from "../../../store/card";
import { useMainStore } from "../../../store/main";
import RecountNumber from "../../../ui/recountNumber/RecountNumber.vue";

const router = useRouter();
const route = useRoute();

const cardStore = useCardStore();
const mainStore = useMainStore();
const themeStore = useThemeStore();
const schedule = useScheduleStore();
const decor = useDecorationStore().screen("product_card");

const props = withDefaults(
	defineProps<{
		qr_menu?: boolean;
		closeProduct?: () => void;
	}>(),
	{
		qr_menu: false,
	},
);

const { stateCard, totalPrice_libCard, isOpenWindow, isAddToCard, isCombo } = storeToRefs(cardStore);
const { isDesktop } = usePdf();

const isActiveBtn = ref(true);
const isNotificationWindows = ref(false);

const btnColor = computed(() => {
	if (isDesktop.value) return false;
	return isActiveBtn.value;
});
const pricePrefix = computed(() => {
	if (props.qr_menu) return null;
	const text = themeStore.data?.kiosk_add_to_cart_text?.trim();
	if (text?.length) return text.length > 20 ? `${text.slice(0, 17)}...` : text;
	return null;
});

const addProductToCart = async () => {
	if (schedule.isClosed) return schedule.notify();
	if (router.options.history.state?.back?.toString().includes("mode=pdf") && props.qr_menu) return;
	if (isActiveBtn.value) {
		cardStore.setCurrentCard(stateCard.value.openCard);
		isNotificationWindows.value = true;
	}

	if (router.options.history.state?.back?.toString().includes("mode=pdf") && props.qr_menu) {
		return;
	}

	if (isActiveBtn.value) {
		cardStore.setCurrentCard(stateCard.value.openCard);
		isNotificationWindows.value = true;

		const groupIndex = stateCard.value.groupModifiers.findIndex((item) => cardStore.checkedRequiredBtn.group.id === item.id);

		if (cardStore.checkedRequiredBtn.haveRequaire) {
			cardStore.toggleNotRequire(cardStore.checkedRequiredBtn.group, groupIndex);
		}
		return;
	}
	const product = stateCard.value.openCard;
	if (!product?.id) return;

	if (!props.qr_menu && cardStore.dataCombo?.length > 0) {
		isCombo.value = true;
		return;
	}

	document.body.style.overflow = "";

	if (stateCard.value.openCard) {
		const productToAdd = buildCartProductFromCardState(stateCard.value as never);

		if (props.closeProduct) props.closeProduct();

		mainStore.addToCart(productToAdd);
		isAddToCard.value = true;
	}

	isOpenWindow.value = false;

	if (props.qr_menu) {
		router.push(`/${route.params.orgId}/`);
	}
};

watch(
	() => stateCard.value.productSizes,
	(sizes) => {
		if (!sizes || sizes.length === 0 || !stateCard.value.selectedSize) return;

		stateCard.value.selectedSize = { ...stateCard.value.selectedSize };
	},
	{ immediate: true },
);

watchEffect(() => {
	if (!cardStore.checkedRequiredBtn.haveRequaire) {
		isActiveBtn.value = false;
	} else {
		isActiveBtn.value = true;
	}
});

onMounted(() => {
	isAddToCard.value = false;
});

defineExpose({ addProductToCart });
</script>

<template>
	<Teleport to="body">
		<NotificationWindow
			:visible="isNotificationWindows"
			:textNotificationDefault="stateCard.textErrorRequaired"
			:backgroundColor="themeStore.data?.accent_color || '#FF6D2E'"
			:buttonAccept="null"
			@update:visible="isNotificationWindows = $event"
		/>
	</Teleport>
	<div class="button_price_bg_product" v-bind="decor.bind('footer')" @touchstart.stop @pointerdown.stop>
		<div class="button_price button_price_modifiers" :class="{ 'button_price_no_width': !!pricePrefix?.length }">
			<NumberModifier v-if="!isDesktop && !stateCard.openCard?.is_marked" :qr_menu="qr_menu" />
			<div
				class="card_product_button_buy"
				:class="{ 'buy_active': !btnColor }"
				data-clickable="true"
				v-bind="decor.bind('buy_button')"
				@click="addProductToCart"
				@touchstart.stop
				@pointerdown.stop
			>
				<img v-if="!isDesktop" :src="cartAddIcon" alt="" class="card_product_button_icon" v-bind="decor.bind('buy_icon')">
				<div class="button_text_container" v-bind="decor.bind('buy_text')">
					<p class="button_text_price" v-bind="decor.bind('price')">
						{{ pricePrefix }}
						<RecountNumber :price="+totalPrice_libCard" :symbol="mainStore.config_lib?.currency_symbol" />
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
    @use "./buttonPriceCount.scss";
</style>
